/**
 * useTodaysHarvests Hook
 *
 * Fetches all harvests recorded today, grouped by strain.
 * Used by QuickHarvestModal to display today's activity panel.
 *
 * Part of Quick Harvest Recording feature enhancement.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '@/supabase-client'
import { toLocalDateString } from '../utils/dateUtils'

/**
 * Hook to fetch today's harvests grouped by strain
 * @returns {Object} { harvestsByStrain, totalKg, loading, error, refetch }
 */
export function useTodaysHarvests() {
  const [harvests, setHarvests] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchTodaysHarvests = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Get today's date in YYYY-MM-DD format
      const today = toLocalDateString(new Date())

      // Fetch harvests from harvest_records table
      const { data, error: fetchError } = await supabase
        .from('harvest_records')
        .select(`
          id,
          harvest_date,
          weight_kg,
          quality_grade,
          created_at,
          production_batch:production_batches (
            id,
            batch_code,
            strain:strains (
              id,
              code,
              name
            )
          )
        `)
        .eq('harvest_date', today)
        .order('created_at', { ascending: false })

      if (fetchError) {
        throw fetchError
      }

      setHarvests(data || [])
    } catch (err) {
      console.error('[useTodaysHarvests] Error:', err)
      setError(err.message)
      setHarvests([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTodaysHarvests()
  }, [fetchTodaysHarvests])

  // Group harvests by strain
  const harvestsByStrain = useMemo(() => {
    const grouped = {}

    for (const harvest of harvests) {
      const strain = harvest.production_batch?.strain
      const strainName = strain?.name || strain?.code || 'Unknown'

      if (!grouped[strainName]) {
        grouped[strainName] = {
          total: 0,
          harvests: []
        }
      }

      // Extract time from created_at
      const createdAt = new Date(harvest.created_at)
      const time = createdAt.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit'
      })

      grouped[strainName].total += parseFloat(harvest.weight_kg) || 0
      grouped[strainName].harvests.push({
        id: harvest.id,
        time,
        weight_kg: parseFloat(harvest.weight_kg) || 0,
        quality_grade: harvest.quality_grade,
        batch_code: harvest.production_batch?.batch_code || 'N/A'
      })
    }

    // Round totals
    for (const strain of Object.keys(grouped)) {
      grouped[strain].total = Math.round(grouped[strain].total * 100) / 100
    }

    return grouped
  }, [harvests])

  // Calculate total kg for today
  const totalKg = useMemo(() => {
    return harvests.reduce((sum, h) => sum + (parseFloat(h.weight_kg) || 0), 0)
  }, [harvests])

  return {
    harvests,
    harvestsByStrain,
    totalKg: Math.round(totalKg * 100) / 100,
    loading,
    error,
    refetch: fetchTodaysHarvests
  }
}

export default useTodaysHarvests
